import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useVault } from '@/lib/supabase/VaultContext';
import { useAuth } from '@/lib/supabase/AuthContext';
import { TagSelector } from './TagSelector';
import { PasswordGenerator } from './PasswordGenerator';
import { TipTapEditor } from './TipTapEditor';
import { Button } from '@/components/ui/Button';
import { getPayloadSchema } from '@/lib/supabase/schemas';
import type { Item, ItemType } from '@/lib/supabase/types';
import { getDownloadUrl } from '@/lib/r2/storage';
import {
  X,
  Edit2,
  Trash2,
  Star,
  Copy,
  Check,
  Eye,
  EyeOff,
  ExternalLink,
  Calendar,
  AlertTriangle,
  Lock,
  Sparkles,
  Loader2,
  KeyRound,
  FileText,
  FileUp,
  FileImage,
  CreditCard,
  UserSquare,
  Binary,
  Globe,
  Mail,
  Download,
  RefreshCw,
} from 'lucide-react';

interface ItemDetailDrawerProps {
  item: Item | null;
  onClose: () => void;
}

export function ItemDetailDrawer({ item, onClose }: ItemDetailDrawerProps) {
  const { updateItem, deleteItem, toggleFavorite } = useVault();
  const { session } = useAuth();
  
  const [isEditing, setIsEditing] = useState(false);
  
  // Edit Form States
  const [title, setTitle] = useState('');
  const [favorite, setFavorite] = useState(false);
  const [payload, setPayload] = useState<Record<string, any>>({});
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  
  // UI States
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showGenerator, setShowGenerator] = useState(false);
  
  // Copy feedback state
  const [copiedField, setCopiedField] = useState<string | null>(null);
  
  // Reveal secrets state
  const [revealedFields, setRevealedFields] = useState<Record<string, boolean>>({});
  
  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmTitle, setDeleteConfirmTitle] = useState('');

  // Download URL state (for file/photo items)
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // Sync state when item changes or edit is toggled
  useEffect(() => {
    if (item) {
      setTitle(item.title);
      setFavorite(item.favorite);
      setPayload(item.payload || {});
      setSelectedTags(item.tags?.map((t) => t.name) || []);
      setError(null);
      setIsEditing(false);
      setShowDeleteConfirm(false);
      setDeleteConfirmTitle('');
      setRevealedFields({});
      // Reset download state when a different item is opened
      setDownloadUrl(null);
      setDownloadError(null);
    }
  }, [item]);

  // Auto-fetch a presigned download URL for file/photo items
  useEffect(() => {
    if (!item?.storage_ref || !session?.access_token) return;
    if (item.item_type !== 'file' && item.item_type !== 'photo') return;

    let cancelled = false;
    setDownloadLoading(true);
    setDownloadError(null);
    setDownloadUrl(null);

    getDownloadUrl(session.access_token, item.storage_ref).then(({ url, error }) => {
      if (cancelled) return;
      if (error) setDownloadError(error);
      else setDownloadUrl(url);
      setDownloadLoading(false);
    });

    return () => { cancelled = true; };
  }, [item?.id, item?.storage_ref, session?.access_token]);

  if (!item) return null;

  const typeLabels: Record<ItemType, string> = {
    file: 'File',
    photo: 'Photo',
    note: 'Secure Note',
    password: 'Password',
    card: 'Credit Card',
    id: 'ID / Document',
    software_key: 'Software Key',
  };

  const typeIcons: Record<ItemType, React.ReactNode> = {
    file: <FileUp className="size-5 text-accent" />,
    photo: <FileImage className="size-5 text-accent" />,
    note: <FileText className="size-5 text-accent" />,
    password: <KeyRound className="size-5 text-accent" />,
    card: <CreditCard className="size-5 text-accent" />,
    id: <UserSquare className="size-5 text-accent" />,
    software_key: <Binary className="size-5 text-accent" />,
  };

  // Secure copy with 30s auto-clear
  function handleCopy(text: string, fieldName: string) {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    
    // Clear the visual checkmark after 2s
    setTimeout(() => {
      setCopiedField(null);
    }, 2000);

    // Clear the system clipboard after 30 seconds for sensitive fields
    if (['password', 'cardNumber', 'cvv', 'licenseKey'].includes(fieldName)) {
      setTimeout(async () => {
        try {
          const currentClipboard = await navigator.clipboard.readText();
          if (currentClipboard === text) {
            await navigator.clipboard.writeText('');
            console.log(`[vault] Clipboard auto-cleared for sensitive field: ${fieldName}`);
          }
        } catch (e) {
          // If we cannot read, just clear it anyway
          await navigator.clipboard.writeText('');
        }
      }, 30000);
    }
  }

  function toggleReveal(field: string) {
    setRevealedFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  }

  // Calculate days remaining for IDs
  function getExpiryStatus(dateStr?: string) {
    if (!dateStr) return null;
    const expiry = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    const diffTime = expiry.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return { label: 'Expired', color: 'text-danger border-danger/20 bg-danger/5', icon: <AlertTriangle className="size-3" /> };
    } else if (diffDays <= 30) {
      return { label: `Expires in ${diffDays}d`, color: 'text-warn border-warn/20 bg-warn/5', icon: <AlertTriangle className="size-3" /> };
    } else {
      return { label: `Valid (${diffDays}d left)`, color: 'text-success border-success/20 bg-success/5', icon: null };
    }
  }

  function updatePayloadField(field: string, value: any) {
    setPayload((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  async function handleSave() {
    if (!item) return;
    if (!title.trim()) {
      setError('Title is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Validate
      const schema = getPayloadSchema(item.item_type);
      const validatedPayload = schema.parse(payload);

      const { error: dbError } = await updateItem(
        item.id,
        {
          title: title.trim(),
          payload: validatedPayload,
          favorite,
        },
        selectedTags
      );

      if (dbError) throw new Error(dbError);
      setIsEditing(false);
    } catch (err: any) {
      setError(err.message || 'Failed to update item.');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete() {
    if (!item) return;
    if (deleteConfirmTitle.trim() !== item.title) {
      setError('Title does not match.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: dbError } = await deleteItem(item.id);
      if (dbError) throw new Error(dbError);
      
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to delete item.');
      setLoading(false);
    }
  }

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-40 flex justify-end p-0">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => {
            if (!loading) onClose();
          }}
          className="fixed inset-0 bg-bg-deep/60 backdrop-blur-sm"
        />

        {/* Drawer Panel */}
        <motion.div
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ type: 'spring', damping: 26, stiffness: 220 }}
          className="relative z-10 flex h-full w-full max-w-md flex-col border-l border-line bg-surface shadow-2xl sm:max-w-lg"
        >
          {/* Header */}
          <div className="flex h-14 items-center justify-between border-b border-line px-5">
            <div className="flex items-center gap-2.5">
              <span className="grid size-8 place-items-center rounded bg-bg border border-line">
                {typeIcons[item.item_type]}
              </span>
              <div>
                <span className="text-3xs uppercase tracking-[0.15em] text-ink-faint">
                  {typeLabels[item.item_type]}
                </span>
                <h3 className="text-xs font-semibold tracking-tight text-ink max-w-[200px] truncate sm:max-w-[280px]">
                  {item.title}
                </h3>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Favorite Toggle */}
              <button
                type="button"
                onClick={() => toggleFavorite(item.id)}
                className={`rounded p-1.5 transition-colors ${
                  item.favorite
                    ? 'text-accent hover:text-accent-soft'
                    : 'text-ink-faint hover:text-ink-muted'
                }`}
                title="Favorite"
              >
                <Star className={`size-4 ${item.favorite ? 'fill-current' : ''}`} />
              </button>

              {/* Edit Toggle */}
              {!isEditing && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="rounded p-1.5 text-ink-faint hover:bg-bg hover:text-ink transition-colors"
                  title="Edit Item"
                >
                  <Edit2 className="size-4" />
                </button>
              )}

              {/* Close Button */}
              <button
                type="button"
                onClick={onClose}
                className="rounded p-1.5 text-ink-faint hover:bg-bg hover:text-ink transition-colors"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-5 space-y-6">
            {error && (
              <div className="rounded border border-danger/20 bg-danger/5 px-3 py-2 text-xs text-danger">
                {error}
              </div>
            )}

            {isEditing ? (
              /* ================= EDIT MODE ================= */
              <div className="space-y-4">
                {/* Title */}
                <div className="space-y-1">
                  <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Title</label>
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs text-ink focus:border-line-strong focus:outline-none transition-colors"
                  />
                </div>

                {/* Password Fields */}
                {item.item_type === 'password' && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Username / Email</label>
                      <input
                        type="text"
                        value={payload.username || ''}
                        onChange={(e) => updatePayloadField('username', e.target.value)}
                        className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs text-ink focus:border-line-strong focus:outline-none transition-colors"
                      />
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center justify-between">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Password</label>
                        <button
                          type="button"
                          onClick={() => setShowGenerator((g) => !g)}
                          className="flex items-center gap-1 text-2xs text-accent hover:text-accent-soft transition-colors"
                        >
                          <Sparkles className="size-3" />
                          <span>{showGenerator ? 'Hide Generator' : 'Generate'}</span>
                        </button>
                      </div>
                      <input
                        type="text"
                        value={payload.password || ''}
                        onChange={(e) => updatePayloadField('password', e.target.value)}
                        className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs font-mono text-ink focus:border-line-strong focus:outline-none transition-colors"
                      />
                    </div>

                    {showGenerator && (
                      <PasswordGenerator
                        onUsePassword={(p) => {
                          updatePayloadField('password', p);
                          setShowGenerator(false);
                        }}
                      />
                    )}

                    <div className="space-y-1">
                      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Website URL</label>
                      <input
                        type="text"
                        value={payload.url || ''}
                        onChange={(e) => updatePayloadField('url', e.target.value)}
                        className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs text-ink focus:border-line-strong focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                )}

                {/* Note Fields */}
                {item.item_type === 'note' && (
                  <div className="space-y-1">
                    <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Note Content</label>
                    <TipTapEditor
                      content={payload.content || ''}
                      onChange={(html) => updatePayloadField('content', html)}
                    />
                  </div>
                )}

                {/* Card Fields */}
                {item.item_type === 'card' && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Cardholder Name</label>
                      <input
                        type="text"
                        value={payload.cardholderName || ''}
                        onChange={(e) => updatePayloadField('cardholderName', e.target.value)}
                        className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs text-ink focus:border-line-strong focus:outline-none transition-colors"
                      />
                    </div>

                    <div className="space-y-1">
                      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Card Number</label>
                      <input
                        type="text"
                        maxLength={19}
                        value={payload.cardNumber || ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\s?/g, '').replace(/(\d{4})/g, '$1 ').trim();
                          updatePayloadField('cardNumber', val);
                        }}
                        className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs font-mono text-ink focus:border-line-strong focus:outline-none transition-colors"
                      />
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Expiry Month</label>
                        <input
                          type="text"
                          maxLength={2}
                          value={payload.expiryMonth || ''}
                          onChange={(e) => updatePayloadField('expiryMonth', e.target.value)}
                          className="w-full rounded-md border border-line bg-bg px-3 py-2 text-center text-xs font-mono text-ink focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Expiry Year</label>
                        <input
                          type="text"
                          maxLength={4}
                          value={payload.expiryYear || ''}
                          onChange={(e) => updatePayloadField('expiryYear', e.target.value)}
                          className="w-full rounded-md border border-line bg-bg px-3 py-2 text-center text-xs font-mono text-ink focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">CVV</label>
                        <input
                          type="text"
                          maxLength={4}
                          value={payload.cvv || ''}
                          onChange={(e) => updatePayloadField('cvv', e.target.value)}
                          className="w-full rounded-md border border-line bg-bg px-3 py-2 text-center text-xs font-mono text-ink focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* ID Fields */}
                {item.item_type === 'id' && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Document Type</label>
                        <input
                          type="text"
                          value={payload.type || ''}
                          onChange={(e) => updatePayloadField('type', e.target.value)}
                          className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs text-ink focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Country</label>
                        <input
                          type="text"
                          value={payload.country || ''}
                          onChange={(e) => updatePayloadField('country', e.target.value)}
                          className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs text-ink focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">ID Number</label>
                        <input
                          type="text"
                          value={payload.number || ''}
                          onChange={(e) => updatePayloadField('number', e.target.value)}
                          className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs font-mono text-ink focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Expiry Date</label>
                        <input
                          type="date"
                          value={payload.expiryDate || ''}
                          onChange={(e) => updatePayloadField('expiryDate', e.target.value)}
                          className="w-full rounded-md border border-line bg-bg px-3 py-1.5 text-xs font-mono text-ink focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Software Key Fields */}
                {item.item_type === 'software_key' && (
                  <div className="space-y-3">
                    <div className="space-y-1">
                      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">License Key</label>
                      <input
                        type="text"
                        value={payload.licenseKey || ''}
                        onChange={(e) => updatePayloadField('licenseKey', e.target.value)}
                        className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs font-mono text-ink focus:border-line-strong focus:outline-none transition-colors"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Registered Email</label>
                        <input
                          type="email"
                          value={payload.email || ''}
                          onChange={(e) => updatePayloadField('email', e.target.value)}
                          className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs text-ink focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                      <div className="space-y-1">
                        <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Version / Edition</label>
                        <input
                          type="text"
                          value={payload.version || ''}
                          onChange={(e) => updatePayloadField('version', e.target.value)}
                          className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs text-ink focus:border-line-strong focus:outline-none transition-colors"
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Purchase URL</label>
                      <input
                        type="text"
                        value={payload.url || ''}
                        onChange={(e) => updatePayloadField('url', e.target.value)}
                        className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs text-ink focus:border-line-strong focus:outline-none transition-colors"
                      />
                    </div>
                  </div>
                )}

                {/* Notes Field (common except for Note/File/Photo items) */}
                {item.item_type !== 'note' && item.item_type !== 'file' && item.item_type !== 'photo' && (
                  <div className="space-y-1">
                    <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Notes</label>
                    <textarea
                      rows={3}
                      value={payload.notes || ''}
                      onChange={(e) => updatePayloadField('notes', e.target.value)}
                      className="w-full rounded-md border border-line bg-bg px-3 py-2 text-xs text-ink focus:border-line-strong focus:outline-none transition-colors resize-none"
                    />
                  </div>
                )}

                {/* Tag Selector */}
                <TagSelector selectedTags={selectedTags} onChange={setSelectedTags} />

                {/* Edit Actions */}
                <div className="flex gap-3 pt-4 border-t border-line">
                  <Button
                    type="button"
                    variant="subtle"
                    onClick={() => setIsEditing(false)}
                    className="flex-1"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={handleSave}
                    className="flex-1"
                    disabled={loading}
                  >
                    {loading ? <Loader2 className="size-4 animate-spin mx-auto" /> : 'Save Changes'}
                  </Button>
                </div>
              </div>
            ) : (
              /* ================= VIEW MODE ================= */
              <div className="space-y-6">
                {/* FILE & PHOTO VIEWER */}
                {(item.item_type === 'file' || item.item_type === 'photo') && (
                  <div className="space-y-4">
                    {/* File Metadata */}
                    {item.storage_meta && (
                      <div className="flex items-center gap-3 rounded-md border border-line bg-bg/30 p-3.5">
                        <span className="grid size-10 place-items-center rounded bg-surface border border-line flex-shrink-0">
                          {item.item_type === 'photo'
                            ? <FileImage className="size-5 text-accent" />
                            : <FileUp className="size-5 text-accent" />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <h5 className="text-xs font-semibold text-ink truncate">
                            {item.storage_meta.original_name || item.title}
                          </h5>
                          <div className="flex gap-3 mt-0.5">
                            <span className="text-3xs text-ink-faint font-mono uppercase">
                              {item.storage_meta.mime?.split('/')[1] || 'file'}
                            </span>
                            <span className="text-3xs text-ink-faint font-mono">
                              {item.storage_meta.size
                                ? `${(item.storage_meta.size / 1024 / 1024).toFixed(2)} MB`
                                : ''}
                            </span>
                            {item.storage_meta.width && item.storage_meta.height && (
                              <span className="text-3xs text-ink-faint font-mono">
                                {item.storage_meta.width}×{item.storage_meta.height}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Loading / Error state */}
                    {downloadLoading && (
                      <div className="flex items-center justify-center gap-2 py-8 text-ink-muted text-xs">
                        <Loader2 className="size-4 animate-spin" />
                        <span>Preparing secure link…</span>
                      </div>
                    )}

                    {downloadError && (
                      <div className="flex items-center gap-2 rounded border border-danger/20 bg-danger/5 px-3 py-2 text-xs text-danger">
                        <AlertTriangle className="size-3.5 flex-shrink-0" />
                        <span>{downloadError}</span>
                        <button
                          type="button"
                          className="ml-auto rounded p-0.5 hover:bg-danger/10 transition-colors"
                          onClick={() => {
                            if (item.storage_ref && session?.access_token) {
                              setDownloadLoading(true);
                              setDownloadError(null);
                              getDownloadUrl(session.access_token, item.storage_ref).then(({ url, error }) => {
                                if (error) setDownloadError(error);
                                else setDownloadUrl(url);
                                setDownloadLoading(false);
                              });
                            }
                          }}
                          title="Retry"
                        >
                          <RefreshCw className="size-3.5" />
                        </button>
                      </div>
                    )}

                    {/* Photo inline preview */}
                    {downloadUrl && item.item_type === 'photo' && (
                      <div className="overflow-hidden rounded-md border border-line bg-bg/20">
                        <img
                          src={downloadUrl}
                          alt={item.title}
                          className="w-full max-h-80 object-contain"
                          loading="lazy"
                        />
                      </div>
                    )}

                    {/* Download button */}
                    {downloadUrl && (
                      <a
                        href={downloadUrl}
                        download={item.storage_meta?.original_name || item.title}
                        className="flex w-full items-center justify-center gap-2 rounded-md border border-line bg-bg/30 py-2.5 text-xs font-medium text-ink hover:border-line-strong hover:bg-bg/60 transition-all"
                      >
                        <Download className="size-4 text-accent" />
                        <span>Download {item.item_type === 'photo' ? 'Photo' : 'File'}</span>
                      </a>
                    )}
                  </div>
                )}

                {/* 1. Category Specific Render Cards */}
                {item.item_type === 'password' && (
                  <div className="divide-y divide-line rounded-md border border-line bg-bg/40">
                    <ReadRow
                      label="Username / Email"
                      value={payload.username}
                      onCopy={() => handleCopy(payload.username, 'username')}
                      copied={copiedField === 'username'}
                      icon={<Mail className="size-3.5 text-ink-faint" />}
                    />
                    <ReadRow
                      label="Password"
                      value={payload.password}
                      onCopy={() => handleCopy(payload.password, 'password')}
                      copied={copiedField === 'password'}
                      isSecret
                      secretRevealed={revealedFields.password}
                      onToggleReveal={() => toggleReveal('password')}
                      icon={<Lock className="size-3.5 text-ink-faint" />}
                    />
                    {payload.url && (
                      <ReadRow
                        label="Website"
                        value={payload.url}
                        onCopy={() => handleCopy(payload.url, 'url')}
                        copied={copiedField === 'url'}
                        isLink
                        icon={<Globe className="size-3.5 text-ink-faint" />}
                      />
                    )}
                  </div>
                )}

                {item.item_type === 'note' && (
                  <div className="rounded-md border border-line bg-bg/20 p-4">
                    <TipTapEditor
                      content={payload.content || ''}
                      onChange={() => {}}
                      editable={false}
                    />
                  </div>
                )}

                {item.item_type === 'card' && (
                  <div className="space-y-4">
                    {/* Visual credit card graphic */}
                    <div className="relative overflow-hidden rounded-xl border border-line bg-gradient-to-br from-surface-raised to-bg p-5 shadow-lg">
                      <div className="flex items-center justify-between">
                        <CreditCard className="size-8 text-accent/80" />
                        <span className="text-2xs font-mono tracking-wider text-ink-faint">SECURE CARD</span>
                      </div>
                      <div className="mt-6 font-mono text-base tracking-widest text-ink select-all">
                        {revealedFields.cardNumber
                          ? payload.cardNumber
                          : `•••• •••• •••• ${payload.cardNumber?.slice(-4) || '••••'}`}
                      </div>
                      <div className="mt-6 flex justify-between items-end">
                        <div>
                          <div className="text-4xs uppercase tracking-wider text-ink-faint">Cardholder</div>
                          <div className="text-xs font-medium tracking-wide text-ink-muted mt-0.5 uppercase">
                            {payload.cardholderName || 'NOT SPECIFIED'}
                          </div>
                        </div>
                        <div className="flex gap-4">
                          <div>
                            <div className="text-4xs uppercase tracking-wider text-ink-faint">Expires</div>
                            <div className="text-xs font-mono text-ink mt-0.5">
                              {payload.expiryMonth || '••'}/{payload.expiryYear || '••••'}
                            </div>
                          </div>
                          <div>
                            <div className="text-4xs uppercase tracking-wider text-ink-faint">CVV</div>
                            <div className="text-xs font-mono text-ink mt-0.5">
                              {revealedFields.cvv ? payload.cvv : '•••'}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Masked data rows */}
                    <div className="divide-y divide-line rounded-md border border-line bg-bg/40">
                      <ReadRow
                        label="Cardholder Name"
                        value={payload.cardholderName}
                        onCopy={() => handleCopy(payload.cardholderName, 'cardholderName')}
                        copied={copiedField === 'cardholderName'}
                      />
                      <ReadRow
                        label="Card Number"
                        value={payload.cardNumber}
                        onCopy={() => handleCopy(payload.cardNumber?.replace(/\s/g, ''), 'cardNumber')}
                        copied={copiedField === 'cardNumber'}
                        isSecret
                        secretRevealed={revealedFields.cardNumber}
                        onToggleReveal={() => toggleReveal('cardNumber')}
                      />
                      <div className="grid grid-cols-2 divide-x divide-line">
                        <ReadRow
                          label="Expiry"
                          value={`${payload.expiryMonth || ''}/${payload.expiryYear || ''}`}
                          onCopy={() => handleCopy(`${payload.expiryMonth}/${payload.expiryYear}`, 'expiry')}
                          copied={copiedField === 'expiry'}
                        />
                        <ReadRow
                          label="CVV"
                          value={payload.cvv}
                          onCopy={() => handleCopy(payload.cvv, 'cvv')}
                          copied={copiedField === 'cvv'}
                          isSecret
                          secretRevealed={revealedFields.cvv}
                          onToggleReveal={() => toggleReveal('cvv')}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {item.item_type === 'id' && (
                  <div className="space-y-4">
                    {/* Expiry Alert Badge */}
                    {payload.expiryDate && (
                      <div className={`flex items-center gap-2 rounded border px-3 py-2 text-xs font-medium ${getExpiryStatus(payload.expiryDate)?.color}`}>
                        {getExpiryStatus(payload.expiryDate)?.icon}
                        <Calendar className="size-3.5" />
                        <span>Document Status: <strong>{getExpiryStatus(payload.expiryDate)?.label}</strong></span>
                      </div>
                    )}

                    <div className="divide-y divide-line rounded-md border border-line bg-bg/40">
                      {payload.type && (
                        <ReadRow
                          label="Document Type"
                          value={payload.type}
                          onCopy={() => handleCopy(payload.type, 'type')}
                          copied={copiedField === 'type'}
                        />
                      )}
                      {payload.country && (
                        <ReadRow
                          label="Country"
                          value={payload.country}
                          onCopy={() => handleCopy(payload.country, 'country')}
                          copied={copiedField === 'country'}
                        />
                      )}
                      <ReadRow
                        label="ID Number"
                        value={payload.number}
                        onCopy={() => handleCopy(payload.number, 'number')}
                        copied={copiedField === 'number'}
                        isSecret
                        secretRevealed={revealedFields.number}
                        onToggleReveal={() => toggleReveal('number')}
                      />
                      {payload.expiryDate && (
                        <ReadRow
                          label="Expiry Date"
                          value={payload.expiryDate}
                          onCopy={() => handleCopy(payload.expiryDate, 'expiryDate')}
                          copied={copiedField === 'expiryDate'}
                        />
                      )}
                    </div>
                  </div>
                )}

                {item.item_type === 'software_key' && (
                  <div className="divide-y divide-line rounded-md border border-line bg-bg/40">
                    <ReadRow
                      label="License Key"
                      value={payload.licenseKey}
                      onCopy={() => handleCopy(payload.licenseKey, 'licenseKey')}
                      copied={copiedField === 'licenseKey'}
                      isSecret
                      secretRevealed={revealedFields.licenseKey}
                      onToggleReveal={() => toggleReveal('licenseKey')}
                    />
                    {payload.email && (
                      <ReadRow
                        label="Registered Email"
                        value={payload.email}
                        onCopy={() => handleCopy(payload.email, 'email')}
                        copied={copiedField === 'email'}
                        icon={<Mail className="size-3.5 text-ink-faint" />}
                      />
                    )}
                    {payload.version && (
                      <ReadRow
                        label="Version / Edition"
                        value={payload.version}
                        onCopy={() => handleCopy(payload.version, 'version')}
                        copied={copiedField === 'version'}
                      />
                    )}
                    {payload.url && (
                      <ReadRow
                        label="Purchase URL"
                        value={payload.url}
                        onCopy={() => handleCopy(payload.url, 'url')}
                        copied={copiedField === 'url'}
                        isLink
                        icon={<Globe className="size-3.5 text-ink-faint" />}
                      />
                    )}
                  </div>
                )}

                {/* 2. Common Notes section */}
                {item.item_type !== 'note' && item.item_type !== 'file' && item.item_type !== 'photo' && payload.notes && (
                  <div className="space-y-1.5">
                    <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Notes</label>
                    <div className="rounded-md border border-line bg-bg/20 p-3 text-xs text-ink-muted leading-relaxed whitespace-pre-wrap">
                      {payload.notes}
                    </div>
                  </div>
                )}

                {/* 3. Tags list */}
                {item.tags && item.tags.length > 0 && (
                  <div className="space-y-1.5">
                    <label className="text-2xs uppercase tracking-[0.15em] text-ink-faint">Tags</label>
                    <div className="flex flex-wrap gap-1.5">
                      {item.tags.map((tag) => (
                        <span
                          key={tag.id}
                          className="rounded border border-line bg-bg px-2 py-0.5 text-2xs text-ink-muted"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* 4. Dates info */}
                <div className="flex justify-between items-center border-t border-line/40 pt-4 text-4xs text-ink-faint uppercase font-mono">
                  <span>Created: {new Date(item.created_at).toLocaleDateString()}</span>
                  <span>Updated: {new Date(item.updated_at).toLocaleDateString()}</span>
                </div>

                {/* 5. Danger Zone (Delete) */}
                <div className="rounded-md border border-danger/15 bg-danger/[0.02] p-4 space-y-3.5">
                  <div className="flex items-center gap-2 text-danger">
                    <AlertTriangle className="size-4" />
                    <span className="text-xs font-semibold">Danger Zone</span>
                  </div>

                  {!showDeleteConfirm ? (
                    <Button
                      type="button"
                      variant="subtle"
                      onClick={() => setShowDeleteConfirm(true)}
                      className="w-full text-danger border-danger/20 bg-transparent hover:bg-danger/10 hover:text-danger text-xs py-2"
                    >
                      <Trash2 className="size-3.5 mr-1.5 inline" />
                      Delete Item
                    </Button>
                  ) : (
                    <div className="space-y-3 animate-fadeIn">
                      <p className="text-2xs text-ink-muted leading-normal">
                        This action is irreversible. To confirm, please type the title of this item (<strong>{item.title}</strong>):
                      </p>
                      <input
                        type="text"
                        value={deleteConfirmTitle}
                        onChange={(e) => setDeleteConfirmTitle(e.target.value)}
                        placeholder="Type item title to confirm"
                        className="w-full rounded border border-danger/20 bg-bg px-3 py-1.5 text-xs text-ink focus:border-danger focus:outline-none font-mono"
                      />
                      <div className="flex gap-2.5">
                        <Button
                          type="button"
                          variant="subtle"
                          onClick={() => {
                            setShowDeleteConfirm(false);
                            setDeleteConfirmTitle('');
                          }}
                          className="flex-1 text-xs py-1.5"
                          disabled={loading}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          variant="primary"
                          onClick={handleDelete}
                          disabled={deleteConfirmTitle !== item.title || loading}
                          className="flex-1 bg-danger hover:bg-danger/90 border-danger text-bg text-xs py-1.5 font-semibold"
                        >
                          {loading ? <Loader2 className="size-3.5 animate-spin mx-auto" /> : 'Confirm Delete'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

/* ================= READ-ONLY ROWS FOR SECURE FIELDS ================= */

interface ReadRowProps {
  label: string;
  value?: string;
  onCopy: () => void;
  copied: boolean;
  isSecret?: boolean;
  secretRevealed?: boolean;
  onToggleReveal?: () => void;
  isLink?: boolean;
  icon?: React.ReactNode;
}

function ReadRow({
  label,
  value = '',
  onCopy,
  copied,
  isSecret = false,
  secretRevealed = false,
  onToggleReveal,
  isLink = false,
  icon,
}: ReadRowProps) {
  if (!value) return null;

  return (
    <div className="flex items-center justify-between p-3 gap-3 text-xs">
      <div className="flex-1 min-w-0 space-y-0.5">
        <span className="text-4xs uppercase tracking-wider text-ink-faint block">{label}</span>
        <div className="flex items-center gap-1.5">
          {icon && <span className="flex-shrink-0">{icon}</span>}
          
          {isSecret ? (
            <span className="font-mono text-ink font-medium select-all truncate">
              {secretRevealed ? value : '••••••••••••'}
            </span>
          ) : isLink ? (
            <a
              href={value.startsWith('http') ? value : `https://${value}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent hover:text-accent-soft hover:underline truncate inline-flex items-center gap-1"
            >
              <span className="truncate">{value}</span>
              <ExternalLink className="size-3 flex-shrink-0" />
            </a>
          ) : (
            <span className="text-ink-muted font-medium truncate select-all">
              {value}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-shrink-0">
        {/* Eye reveal toggle */}
        {isSecret && onToggleReveal && (
          <button
            type="button"
            onClick={onToggleReveal}
            className="rounded p-1 text-ink-faint hover:bg-bg hover:text-ink-muted transition-colors"
            title={secretRevealed ? 'Hide secret' : 'Reveal secret'}
          >
            {secretRevealed ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
          </button>
        )}

        {/* Copy button */}
        <button
          type="button"
          onClick={onCopy}
          className={`rounded p-1 transition-all ${
            copied
              ? 'text-success bg-success/5 border border-success/10'
              : 'text-ink-faint hover:bg-bg hover:text-ink-muted border border-transparent'
          }`}
          title="Copy to Clipboard"
        >
          {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
        </button>
      </div>
    </div>
  );
}
